import { Box, Checkbox, Divider, SelectChangeEvent, Typography } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { ModalCloseIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import PageHeader from '@src/components/PageHeader/PageHeader';
import { Image } from '@src/interfaces/type';
import React, { useState } from 'react';
import styled from 'styled-components';
import { OrderView } from '..';
import { generateUniqueID } from '@src/helpers/Helpers';
import { OrderProduct } from '../acceptDelivery';
import { useAppDispatch } from '@src/store/hooks';
import { setPreviewImages } from '@src/store/reducers/UIReducer';
import StyledStatus from './StyledStatus';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	letter-spacing: 0.28px;
	width: 100%;
	#mid {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
		padding: 12px;
		height: auto;
		overflow: auto;
		#product-info {
			display: flex;
			gap: 16px;
		}
		#field {
			display: flex;
			gap: 12px;
			span {
				font-weight: 600;
				color: #5c5c5c;
				font-size: 14px;
			}
		}
		#comment-field {
			display: flex;
			flex-direction: column;
			gap: 8px;
		}
		p {
			font-weight: 600;
			color: var(--default-text-color);
			font-size: 14px;
		}
		#red-color {
			color: #f26184;
		}
	}
`;

const CommentContainer = styled.div`
	color: var(--default-text-color);
	font-size: 14px;
	font-style: italic;
	font-weight: 600;
	line-height: 17.6px;
	word-wrap: break-word;
	max-height: 320px;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	width: 100%;
	height: 68px;
	padding: 20px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 100;
	position: fixed;
	bottom: 0;
`;

interface ProductQualityCheckProps {
	windowDimensions: any;
	handleClose: any;
	setView: (data: OrderView) => void;
	productDetails: OrderProduct;
}

const ProductApprovalDetails: React.FC<ProductQualityCheckProps> = ({
	windowDimensions,
	handleClose,
	setView,
	productDetails,
}) => {
	const [product, setProduct] = useState({
		quantity: '',
		issue: '',
		comment: '',
	});

	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>(
		productDetails?.delivery_acc_image
			? [{ id: generateUniqueID(), url: productDetails?.delivery_acc_image, hover: false }]
			: []
	);
	const isMobileWidth = IsMobileWidth();
	const handleChange = () => {};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const filesData = event?.target?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];

		if (imageCount <= 3) {
			if (filesData) {
			}
			setFiles([...files]);
			setImages(imagesData);
		} else {
			// displayError('Maximum of 3 images allowed');
		}
	};

	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const filesData = event.dataTransfer?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];

		if (imageCount <= 3) {
			if (filesData) {
			}
			setFiles([...files]);
			setImages(imagesData);
		} else {
			// displayError('Maximum of 3 images allowed');
		}
	};

	const handleArrowBack = () => {
		setView('orderDetails');
	};

	const fieldsStyle1 = { width: '84vw', height: '36px' };
	const fieldsStyle2 = { width: '198px', height: '36px' };

	const dispatch = useAppDispatch();

	const openPreview = () => {
		dispatch(setPreviewImages([images?.[0]?.url]));
	};

	if (productDetails?.delivery_acc_status?.toLowerCase() === 'accepted') {
		return (
			<StyledContainer>
				<div id="mid">
					<div id="product-info">
						<p>Total Qty: ({productDetails?.quantity})</p>
					</div>
					<div id="field">
						<span>Delivery Status:</span>
						<StyledStatus status={productDetails?.delivery_acc_status?.toLowerCase()} />
					</div>

					<div id="comment-field">
						<span>Comment:</span>
						<CommentContainer>
							{productDetails?.delivery_acc_comments || 'No Comments Found'}
						</CommentContainer>
					</div>
				</div>
			</StyledContainer>
		);
	}

	return (
		<StyledContainer>
			<div id="mid">
				<div id="product-info">
					<p>Total Qty: ({productDetails?.quantity})</p>
					<div id="field">
						<span>Quantity:</span>
						<p> {productDetails?.delivery_acc_quantity}</p>
					</div>
				</div>
				<div id="field">
					<span>Delivery Status:</span>
					<StyledStatus status={productDetails?.delivery_acc_status?.toLowerCase()} />
				</div>
				<div id="field">
					<span>Possible Issue:</span>
					<p id="red-color">{productDetails?.delivery_acc_reason}</p>
				</div>
				<div id="field">
					<span>Resolution:</span>
					<p> {productDetails?.delivery_acc_resolution}</p>
				</div>
				<div id="comment-field">
					<span>Comment:</span>
					<CommentContainer>
						{productDetails?.delivery_acc_comments || 'No Comments Found'}
					</CommentContainer>
				</div>
				<ImageThumnail
					images={images}
					setImages={setImages}
					setFiles={setFiles}
					style={{ width: windowDimensions?.width < 800 ? '84vw' : '100%', height: '140px' }}
					handleFileChange={handleFileChange}
					windowDimensions={windowDimensions}
					openPreview={openPreview}
					readOnly
				/>
			</div>

			{/* <StyledBottomBtnContainer>
				<CustomButton
					type="submit"
					title="Cancel"
					handleClick={() => {}}
					styles={{
						width: '132px',
						height: '36px',
						background: 'var(--btn-color-grey)',
						color: 'var(--default-text-color)',
						':hover': {
							background: 'var(--btn-hover-color) !important',
						},
					}}
				/>
				<CustomButton
					type="submit"
					title="Save"
					handleClick={() => {}}
					styles={{
						width: '132px',
						height: '36px',
					}}
				/>
			</StyledBottomBtnContainer> */}
		</StyledContainer>
	);
};

export default ProductApprovalDetails;
